/**
 * Classic — the layout every user had: the menu tree in a resizable sidebar
 * (same width / hidden prefs keys as before), the open screen beside it.
 * Enhanced per board "0 · Current": the sidebar's Menu / Pinned / Recent /
 * Editors views, a title bar with ⓘ and the pin star instead of the object
 * name, and the open-editors strip along the bottom.
 *
 * At phone width every layout renders this one, with the sidebar as a drawer.
 */
import { Menu } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { tw } from '../../testHooks'
import { EditorsList, EditorsTray } from '../nav/parts/EditorsList'
import { EmptyPane } from '../nav/parts/EmptyPane'
import { MenuTree } from '../nav/parts/MenuTree'
import { ScreenTitle } from '../nav/parts/ScreenTitle'
import { PinnedList, RecentList } from '../nav/parts/ShellLists'
import { ShellFrame } from '../nav/ShellFrame'
import { S, iconButton } from '../nav/tokens'
import type { ShellLayoutProps } from './types'

// Persisted sidebar prefs — the SAME keys the Run view has always used, so
// existing users keep their width / hidden choices.
const NAV_WIDTH_KEY = 'twasta:runNavWidth'
const NAV_COLLAPSED_KEY = 'twasta:runNavCollapsed'
const NAV_MIN_WIDTH = 180
const NAV_MAX_WIDTH = 480
/** Dragging the edge narrower than this lets go of the panel: it hides. */
const NAV_HIDE_BELOW = 120

type Tab = 'menu' | 'pinned' | 'recent' | 'editors'

export default function ClassicLayout({
  model, appTitle, themeColor, compact = false, renderObject, headerExtras,
  emptyNavMessage, emptyPaneMessage,
}: ShellLayoutProps) {
  const accent = themeColor || S.accent
  const phone = model.isPhone
  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(NAV_COLLAPSED_KEY) === '1' } catch { return false }
  })
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [tab, setTab] = useState<Tab>('menu')
  const toggleNavCollapsed = useCallback(() => {
    if (phone) { setDrawerOpen((v) => !v); return }
    setNavCollapsed((prev) => {
      const next = !prev
      try { localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0') } catch { /* ignore */ }
      return next
    })
  }, [phone])
  const rootRef = useRef<HTMLDivElement>(null)
  const asideRef = useRef<HTMLElement>(null)
  const [navWidth, setNavWidth] = useState<number>(() => {
    try {
      const saved = localStorage.getItem(NAV_WIDTH_KEY)
      if (saved) {
        const n = parseInt(saved, 10)
        if (!Number.isNaN(n)) return Math.max(NAV_MIN_WIDTH, Math.min(NAV_MAX_WIDTH, n))
      }
    } catch { /* ignore */ }
    return compact ? 220 : 280
  })
  useEffect(() => {
    try { localStorage.setItem(NAV_WIDTH_KEY, String(navWidth)) } catch { /* ignore */ }
  }, [navWidth])
  // One drag gesture for the panel's whole range: wider/narrower between the
  // min and max, let go below NAV_HIDE_BELOW to hide it entirely, and — from
  // the thin strip left behind when hidden — drag back out to bring it back.
  const startNavResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startLeft = (asideRef.current ?? rootRef.current)?.getBoundingClientRect().left ?? 0
    const onMove = (ev: MouseEvent) => {
      const raw = ev.clientX - startLeft
      const hide = raw < NAV_HIDE_BELOW
      setNavCollapsed((prev) => {
        if (prev === hide) return prev
        try { localStorage.setItem(NAV_COLLAPSED_KEY, hide ? '1' : '0') } catch { /* ignore */ }
        return hide
      })
      if (!hide) setNavWidth(Math.max(NAV_MIN_WIDTH, Math.min(NAV_MAX_WIDTH, raw)))
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [])

  // Phone: the drawer starts open only when there is nothing on screen.
  useEffect(() => { if (phone) setDrawerOpen(!model.current) }, [phone]) // eslint-disable-line react-hooks/exhaustive-deps
  const closeDrawer = useCallback(() => { if (phone) setDrawerOpen(false) }, [phone])
  // "Open editors" from the cap notice: the Editors view, with the panel shown.
  useEffect(() => {
    if (!model.editorsListRequest) return
    setTab('editors')
    if (phone) setDrawerOpen(true)
    else setNavCollapsed(false)
  }, [model.editorsListRequest]) // eslint-disable-line react-hooks/exhaustive-deps

  const headerHeight = compact ? 44 : 52
  const current = model.current
  const navShown = phone ? drawerOpen : !navCollapsed
  const full = model.fullScreen

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'menu', label: 'Menu' },
    { id: 'pinned', label: 'Pinned', count: model.pinned.length },
    { id: 'recent', label: 'Recent', count: model.recent.length },
    ...(model.editorsEnabled ? [{ id: 'editors' as Tab, label: 'Editors', count: model.editors.length }] : []),
  ]
  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex((t) => t.id === tab)
    const next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1
      : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : null
    if (next === null) return
    e.preventDefault()
    const t = tabs[(next + tabs.length) % tabs.length]
    setTab(t.id)
    ;(e.currentTarget.querySelector(`[data-tab="${t.id}"]`) as HTMLElement | null)?.focus()
  }

  const hamburger = (label: string) => (
    <button type="button" className="tw-sh-btn" onClick={toggleNavCollapsed} title={label} aria-label={label}
      {...tw('action', 'shell-nav-toggle')} style={iconButton(34)}>
      <Menu size={17} aria-hidden />
    </button>
  )

  const sidebar = (
    <nav
      ref={asideRef as React.RefObject<HTMLElement>}
      aria-label="Navigation"
      style={phone ? {
        position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 9420, width: 'min(320px, 86vw)',
        background: S.panel, borderRight: `1px solid ${S.border}`, display: 'flex', flexDirection: 'column',
        boxShadow: '0 0 40px rgba(0,0,0,0.3)',
      } : {
        width: navWidth, flexShrink: 0, position: 'relative', background: S.panel,
        borderRight: `1px solid ${S.border}`, display: 'flex', flexDirection: 'column',
        overflow: 'hidden', minHeight: 0,
      }}
    >
      <div style={{
        height: headerHeight, boxSizing: 'border-box', padding: compact ? '0 0.5rem 0 0.75rem' : '0 0.5rem 0 0.875rem',
        borderBottom: `1px solid ${S.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: S.text2 }}>
          Navigation
        </span>
        <button type="button" className="tw-sh-btn" onClick={toggleNavCollapsed}
          title="Hide navigation" aria-label="Hide navigation" style={iconButton(34)}>
          <Menu size={17} aria-hidden />
        </button>
      </div>
      <div role="tablist" aria-label="Navigation view" onKeyDown={onTabKey} style={{
        display: 'flex', gap: 3, margin: '10px 10px 6px', padding: 3, borderRadius: 9, background: S.surface2,
        flexShrink: 0,
      }}>
        {tabs.map((t) => {
          const on = tab === t.id
          return (
            <button key={t.id} type="button" role="tab" data-tab={t.id} aria-selected={on}
              tabIndex={on ? 0 : -1} onClick={() => setTab(t.id)}
              {...tw('action', `shell-tab:${t.id}`, { selected: on })}
              style={{
                flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                height: 32, border: 'none', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit',
                background: on ? S.panel : 'transparent', color: on ? S.text : S.text2,
                fontSize: 13.5, fontWeight: on ? 600 : 500, boxShadow: on ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
              }}>
              <span className="tw-sh-clip">{t.label}</span>
              {typeof t.count === 'number' && t.count > 0 && (
                <span style={{ fontSize: 13, color: S.text2, fontWeight: 500 }}>{t.count}</span>
              )}
            </button>
          )
        })}
      </div>
      <div role="tabpanel" aria-label={tabs.find((t) => t.id === tab)?.label}
        style={{ flex: 1, overflow: 'auto', padding: '0.25rem 0.375rem 1rem', minHeight: 0 }}>
        {tab === 'menu' && (model.roots.length === 0
          ? <div style={{ padding: 16, fontSize: 14, color: S.text2, lineHeight: 1.5 }}>{emptyNavMessage}</div>
          : <MenuTree model={model} onPicked={closeDrawer} />)}
        {tab === 'pinned' && <PinnedList model={model} onPicked={closeDrawer} />}
        {tab === 'recent' && <RecentList model={model} onPicked={closeDrawer} />}
        {tab === 'editors' && <EditorsList model={model} onPicked={closeDrawer} />}
      </div>
      {!phone && (
        <div
          onMouseDown={startNavResize}
          title="Drag to resize — drag all the way left to hide"
          style={{ position: 'absolute', top: 0, right: 0, width: 5, height: '100%', cursor: 'col-resize', zIndex: 5 }}
          onMouseEnter={(e) => { e.currentTarget.style.background = accent }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
        />
      )}
    </nav>
  )

  return (
    <div ref={rootRef} style={{ display: 'flex', flex: 1, width: '100%', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {!full && navShown && sidebar}
      {!full && phone && drawerOpen && (
        <div onClick={() => setDrawerOpen(false)} aria-hidden
          style={{ position: 'fixed', inset: 0, zIndex: 9419, background: 'rgba(0,0,0,0.35)' }} />
      )}
      {!full && !phone && navCollapsed && (
        <div
          onMouseDown={startNavResize}
          title="Drag to show navigation"
          style={{ width: 6, flexShrink: 0, cursor: 'col-resize', borderRight: `1px solid ${S.border}`, background: S.panel }}
          onMouseEnter={(e) => { e.currentTarget.style.background = accent }}
          onMouseLeave={(e) => { e.currentTarget.style.background = S.panel }}
        />
      )}
      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, background: S.bg }}>
        {current && !full && (
          <ScreenTitle
            item={current}
            height={headerHeight}
            compact={compact}
            pinned={model.isPinned(current)}
            onTogglePin={() => model.togglePin(current)}
            fullScreen={full}
            onToggleFullScreen={() => model.setFullScreen(!full)}
            onClose={model.close}
            extras={headerExtras?.(current)}
            leading={(!navShown) ? hamburger('Show navigation') : undefined}
          />
        )}
        <ShellFrame
          model={model}
          renderObject={renderObject}
          padding={compact ? '0.5rem' : '0.5rem 1rem 1rem'}
          empty={(
            <EmptyPane model={model} appTitle={appTitle} message={emptyPaneMessage} accent={themeColor}
              leading={!navShown ? hamburger('Show navigation') : undefined} />
          )}
        />
        {!full && model.editorsEnabled && <EditorsTray model={model} />}
      </main>
    </div>
  )
}
